import { Component, OnInit } from '@angular/core';

type DeviceType = 'ios' | 'android' | 'desktop';

/**
 * Desktop modal için localStorage key.
 * Kullanıcı modalı kapattığında kalıcı olarak işaretlenir, bir daha gösterilmez.
 *
 * Banner için localStorage kullanılmaz — her ziyarette gösterilmesi doğru UX'tir:
 * uygulama yüklüyse appi açar, değilse mağazaya götürür.
 */
const MODAL_KEY = 'cdm_modal_dismissed';

/** Kullanıcı daha önce kapattı mı? */
function isDismissed(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

/** Kalıcı olarak kapatıldı işaretle. */
function recordDismissal(key: string): void {
  try {
    localStorage.setItem(key, 'true');
  } catch { /* ignore */ }
}

// SPA içi navigasyonda ikinci kez açılmayı önleyen oturum-içi guard'lar.
let modalShownThisSession = false;
let bannerShownThisSession = false;

@Component({
  selector: 'app-banner-modal',
  templateUrl: './app-banner-modal.component.html',
  styleUrls: ['./app-banner-modal.component.css']
})
export class AppBannerModalComponent implements OnInit {
  deviceType: DeviceType = 'desktop';

  /** Desktop duyuru modalı */
  modalVisible = false;

  /** Mobil uygulama banner'ı */
  bannerVisible = false;

  readonly appStoreUrl = 'https://apps.apple.com/tr/app/cardata/id1187720629';
  readonly playStoreUrl = 'https://play.google.com/store/apps/details?id=com.cardata';

  ngOnInit(): void {
    this.deviceType = this.detectDevice();
    if (this.deviceType === 'desktop') {
      this.initModal();
    } else {
      this.initBanner();
    }
  }

  private detectDevice(): DeviceType {
    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.indexOf('Macintosh') !== -1 && 'ontouchend' in document);
    if (isIOS) { return 'ios'; }
    if (/android/i.test(ua)) { return 'android'; }
    return 'desktop';
  }

  /**
   * Kullanıcı uygulamanın kendi WebView'ından mı giriyor?
   * Eğer öyleyse zaten uyguladalar → "Uygulamada Aç" banner'ını gösterme.
   *
   * Android WebView UA'sında "wv" veya "Version/x.x" olmadan "Chrome" içerir.
   * iOS WKWebView UA'sında "Safari" kelimesi geçmez.
   */
  private isWebView(): boolean {
    const ua = navigator.userAgent || '';
    // Android: WebView UA'sında '; wv)' geçer
    if (/android/i.test(ua) && /wv\)/.test(ua)) { return true; }
    // iOS: AppleWebKit var ama Safari yok → WKWebView
    if (/AppleWebKit/i.test(ua) && !/Safari/i.test(ua)) { return true; }
    return false;
  }

  private isHome(): boolean {
    const path = window.location.pathname;
    return path === '/' || path === '/default';
  }

  private initModal(): void {
    // Sadece ana sayfada göster.
    if (!this.isHome()) { return; }
    // Bu oturumda zaten açıldıysa tekrar açma.
    if (modalShownThisSession) { return; }
    // Kullanıcı daha önce kalıcı olarak kapattıysa gösterme.
    if (isDismissed(MODAL_KEY)) { return; }

    setTimeout(() => {
      modalShownThisSession = true;
      this.modalVisible = true;
    }, 600);
  }

  private initBanner(): void {
    // Kullanıcı zaten uygulamanın içindeyse (WebView) banner'ı hiç gösterme.
    if (this.isWebView()) { return; }
    // Bu oturumda zaten açıldıysa tekrar açma (SPA navigasyon guard).
    if (bannerShownThisSession) { return; }
    // localStorage kontrolü yok: her ziyarette gösterilmesi doğru UX'tir.

    setTimeout(() => {
      bannerShownThisSession = true;
      this.bannerVisible = true;
    }, 400);
  }

  closeModal(): void {
    this.modalVisible = false;
    // Kalıcı olarak kapat; bir daha gösterilmez.
    recordDismissal(MODAL_KEY);
  }

  onOverlayClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('cdm-overlay')) {
      this.closeModal();
    }
  }

  closeBanner(): void {
    this.bannerVisible = false;
    // localStorage'a yazmıyoruz: bir sonraki ziyarette tekrar gösterilmeli.
  }

  /**
   * Uygulamayı açar; yüklü değilse store'a yönlendirir.
   * - Android: intent:// URL (uygulama yoksa Play Store'a browser_fallback_url ile düşer).
   * - iOS: cardata.com.tr aynı domain olduğundan Universal Links çalışmaz;
   *   custom scheme deneriz, uygulama açılmazsa App Store'a yönlendiririz.
   */
  openApp(): void {
    if (this.deviceType === 'android') {
      const fallback = encodeURIComponent(this.playStoreUrl);
      window.location.href =
        'intent://#Intent;scheme=cardata;package=com.cardata;S.browser_fallback_url=' + fallback + ';end';
    } else if (this.deviceType === 'ios') {
      this.openIosAppOrStore();
    }
  }

  private openIosAppOrStore(): void {
    const start = Date.now();
    // Uygulama açıldıysa sayfa arka plana geçer ve timer geç çalışır;
    // elapsed-time guard yanlış yönlendirmeyi önler.
    setTimeout(() => {
      if (Date.now() - start < 2000 && !document.hidden) {
        window.location.href = this.appStoreUrl;
      }
    }, 1200);
    window.location.href = 'cardata://';
  }
}
